import { Instagram, MapPin, Phone, Mail } from "lucide-react";
import { NAV_LINKS, SITE, IMAGES } from "@/lib/site";

const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0f2b1f] text-white pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="w-14 h-14 rounded-full overflow-hidden bg-white border-2 border-[#D4AF37] shadow-md flex items-center justify-center">
              <img
                src={IMAGES.logo}
                alt="Navilukallu Homestay logo"
                className="w-full h-full object-contain p-1"
                loading="lazy"
              />
            </span>
            <div>
              <div className="font-heading text-2xl">Navilukallu</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
                Homestay
              </div>
            </div>
          </div>
          <p className="mt-5 text-white/70 max-w-sm text-sm md:text-base leading-relaxed">
            {SITE.tagline}. A luxury Malnad homestay nestled inside a coffee
            estate between Belur & Sakleshpur, Western Ghats.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a>
            <a
              href={SITE.mapsShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-maps"
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              aria-label="Google Maps"
            >
              <MapPin size={18} />
            </a>
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="footer-phone"
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              aria-label="Phone"
            >
              <Phone size={18} />
            </a>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="footer-email"
              className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              aria-label="Email"
            >
              <Mail size={18} />
            </a>
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
            Explore
          </div>
          <ul className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm text-white/75">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  className="hover:text-[#D4AF37] transition-colors"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
            Reach Us
          </div>
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex items-start gap-2">
              <MapPin size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <a
                href={SITE.mapsShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-address-link"
                className="hover:text-[#D4AF37]"
              >
                {SITE.address}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Phone size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <a href={`tel:${SITE.phoneRaw}`} className="hover:text-[#D4AF37]">
                {SITE.phoneDisplay}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <a
                href={`mailto:${SITE.email}`}
                className="hover:text-[#D4AF37] break-all"
                data-testid="footer-email-link"
              >
                {SITE.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Instagram size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="hover:text-[#D4AF37]"
              >
                @navilukalluhomestay
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-14 border-t border-white/10 pt-6 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-white/50">
        <div>© 2026 Navilukallu Homestay. All rights reserved.</div>
        <div>
          Made by{" "}
          <a
            href={SITE.portfolioUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-creator-link"
            className="text-[#D4AF37] hover:text-white transition-colors underline decoration-[#D4AF37]/40 underline-offset-4"
          >
            {SITE.creatorName}
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
